import styled, { css, ThemeContext } from 'styled-components';
import { useContext } from 'react';
import rem from 'polished/lib/helpers/rem';
import Button from '../../base/Button';
import { Container, Col } from '../../base/Grid';
import Picture from '../../base/Picture';
import Typography from '../../base/Typography';

const getSectionVariantBgColor = (theme, variant) => {
  if (variant === 'primary') {
    return theme.colors.primary.blue;
  }
  if (variant === 'secondary') {
    return theme.colors.neutral.gray95;
  }
  // If a hex color code is passed in, use that. (Design frequently uses several gray shades)
  if (variant && variant !== 'tertiary') {
    return variant;
  }
  // tertiary (Default)
  return theme.colors.neutral.white;
};

const getComponentVariantBgColor = (theme, variant) => {
  if (variant === 'primary') {
    return theme.colors.primary.blue;
  }
  if (variant === 'secondary') {
    return 'transparent';
  }
  return theme.colors.neutral.gray99;
};

const getComponentVariantTextColor = (theme, variant) => {
  if (variant === 'primary') {
    return theme.colors.neutral.white;
  }
  return theme.colors.neutral.gray45;
};

export const SectionExperienceCalloutStyled = styled.section`
  && {
    ${({ theme, variant, hasFullWidthContainer }) => {
    let marginVertical;
    let paddingHorizontal;
    const xlargeUp = theme.useMediaQuery(theme.breakpoints.up('xlarge'));
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));

    if (hasFullWidthContainer) {
      marginVertical = 0;
      paddingHorizontal = 0;
    } else if (mediumUp) {
      marginVertical = rem(60);
    } else {
      marginVertical = rem(50);
    }

    if (!hasFullWidthContainer) {
      if (xlargeUp) {
        paddingHorizontal = 0;
      } else if (mediumUp) {
        paddingHorizontal = rem(25);
      } else {
        paddingHorizontal = rem(12);
      }
    }

    return css`
      position: relative;
      padding-left: ${paddingHorizontal};
      padding-right: ${paddingHorizontal};
      margin-top: ${marginVertical};
      margin-bottom: ${marginVertical};
      max-width: unset;
      background-color: ${getSectionVariantBgColor(theme, variant)};
    `;
  }}
  }
}`;

export const ContainerExperienceCalloutStyled = styled(Container)`
  ${({
    theme, variant, isFullWidthContainer, withBGImage,
  }) => {
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    let background = 'transparent';

    if (isFullWidthContainer || !mediumUp) {
      background = getComponentVariantBgColor(theme, variant);
    }

    return css`
      background-color: ${background};
      padding: 0;
      ${(withBGImage && !isFullWidthContainer) && 'position: relative;'}
  `;
  }
}
`;

export const TypographyTitleStyled = styled(Typography)` 
  ${({ theme }) => {
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    return css`
    padding-bottom: ${mediumUp
    ? rem(16)
    : rem(12)};
`;
  }
}
`;

export const DescriptionCopyTypographyStyled = styled(Typography)`
  padding-bottom: ${rem(24)};
`;

export const ComponentContainerExperienceCalloutStyled = styled.div`
  ${({
    theme, variant, isFullWidthContainer, withHoverShadow,
  }) => {
    let boxShadow = 'none';

    if (withHoverShadow) {
      boxShadow = theme.shadows.medium;
    }

    return css`
      ${!isFullWidthContainer && 'position:relative'};
      transition: ${theme.shadows.transition};
      background-color: ${getComponentVariantBgColor(theme, variant)};
      color: ${getComponentVariantTextColor(theme, variant)};
      &:hover {
        box-shadow: ${boxShadow};
        text-decoration: none;
        .button-experience-callout-styled {
          /** Inner button Hover styles */
          border-color: ${theme.colors.cta.blueHover};
          color: ${theme.colors.cta.blueHover};
        } 
    `;
  }
}`;

export const ColExperienceCalloutStyled = styled(Col)`
  display: flex;
  align-items: center;
  text-align: center;
  padding: 0;
`;

export const CTAContainerExperienceCalloutStyled = styled.div`
  ${({ theme, withBGImage }) => {
    const mediumUp = theme.useMediaQuery(theme.breakpoints.up('medium'));
    let padding = `0 ${rem(32)} ${rem(48)}`;

    if (withBGImage || mediumUp) {
      padding = `${rem(48)} ${rem(32)}`;
    }

    return css`
      padding: ${padding};
  `;
  }
}
`;

// Loaded for the BG Image variant
export const BGImageContainerExperienceCalloutStyled = styled.div`
  ${({ theme, forFullWidthContainer }) => {
    const largeUp = theme.useMediaQuery(theme.breakpoints.up('large'));
    const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
    let width = '100%';
    let height = '100%';
    let position = 'relative';

    if (forFullWidthContainer && largeUp) {
      // if it IS fullWidth, set the BG image to the full screen orientation
      width = rem(460);
      position = 'absolute';
    }
    if (mediumDown) {
      height = rem(180);
    }

    return css`
      position: ${position};
      top: 0;
      left: 0;
      height: ${height};
      width: ${width};
      background-size: cover;
      background-position: center;
      display: flex;
      align-items: center;
      padding: 0;
    `;
  }
}`;

export const ImageContainerExperienceCalloutStyled = styled.div`
  ${({ theme }) => {
    const mediumDown = theme.useMediaQuery(theme.breakpoints.down('medium'));
    const largeUp = theme.useMediaQuery(theme.breakpoints.up('large'));
    let padding = 0;
    if (mediumDown) {
      padding = `${rem(48)} ${rem(32)} ${rem(32)}`;
    }
    return css`
      position: relative;
      height: auto;
      width: 100%;
      display: flex;
      align-items: center;
      padding: ${padding};
      ${largeUp && 'justify-content: center;'}
    `;
  }
}`;

export const PictureExperienceCalloutStyled = styled(Picture)`
  && {
    height: ${rem(200)};
    width: 100%;
    display: flex;
    justify-content: center;
  }
}`;

export const ButtonExperienceCalloutStyled = styled(Button)`
  ${({ theme, variantOverride }) => {
    let color = theme.colors.cta.blue;
    if (variantOverride === 'primary') {
      color = theme.colors.neutral.white;
    }
    return css`
      && {
        background-color: transparent;
        color: ${color};
        border-color: ${color};
      }
    `;
  }
}`;

export const ButtonOverlayStyled = styled.button`
  width: 100%;
  height: 100%;
  opacity: 0;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
  :hover {
    cursor: pointer;
  }  
}`;

/** Creates inline styles (to override stock Component Library styles) */
export const Styles = () => {
  const theme = useContext(ThemeContext);

  const largeUp = theme.useMediaQuery(theme.breakpoints.up('large'));
  const hw = largeUp ? '80%' : '100%';
  return {
    IconExperienceCalloutStyled: {
      height: hw,
      width: hw,
      display: 'flex',
      justifyContent: 'center',
    },
  };
};
